import { Fragment, useEffect, useRef } from "react";
import { useRootData } from "~/utils/data/useRootData";

interface Props {
  children: React.ReactNode;
  enabled: boolean;
  onVerify?: (token: string) => void;
  formId?: string; // Add optional form ID to target specific form
}

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      render: (element: HTMLElement | string, options: any) => number;
    };
    onRecaptchaLoad: () => void;
  }
}

export default function RecaptchaWrapper({ children, enabled, onVerify, formId }: Props) {
  const rootData = useRootData();
  const siteKey = (rootData.appConfiguration.auth.recaptcha.siteKey && enabled) ? rootData.appConfiguration.auth.recaptcha.siteKey : undefined;
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!enabled || !siteKey) return;

    // Detect if the site key appears to be v3 (starts with 6L)
    const isV3 = siteKey.startsWith('6L');

    // Load reCAPTCHA script if it's not already loaded
    if (!document.querySelector('script[src*="recaptcha"]')) {
      const script = document.createElement('script');
      
      if (isV3) {
        // Use reCAPTCHA v3
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      } else {
        // Use reCAPTCHA v2 with explicit rendering
        script.src = `https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit`;
      }
      
      script.async = true;
      script.defer = true;

      // Define the callback function for v2
      if (!isV3) {
        window.onRecaptchaLoad = () => {
          if (recaptchaRef.current && window.grecaptcha) {
            widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
              sitekey: siteKey,
              callback: (token: string) => {
                if (onVerify) onVerify(token);
                injectTokenIntoForm(token);
              },
              'expired-callback': () => {
                console.log('reCAPTCHA expired');
              }
            });
          }
        };
      }

      script.onload = () => {
        if (isV3 && window.grecaptcha) {
          executeV3Recaptcha();
        }
      };

      script.onerror = () => {
        console.error('Failed to load reCAPTCHA script');
      };

      document.head.appendChild(script);
    } else if (isV3 && window.grecaptcha) {
      // Script already loaded, execute v3 directly
      executeV3Recaptcha();
    } else if (!isV3 && window.grecaptcha && window.grecaptcha.render && recaptchaRef.current && !widgetIdRef.current) {
      // If script is already loaded but widget hasn't been rendered yet (for v2)
      widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          if (onVerify) onVerify(token);
          injectTokenIntoForm(token);
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
        }
      });
    }

    function executeV3Recaptcha() {
      if (!siteKey) {
        console.error("reCAPTCHA site key is not defined for v3.");
        return;
      }
      window.grecaptcha.ready(() => {
        try {
          window.grecaptcha.execute(siteKey, { action: 'submit' })
            .then((token: string) => {
              if (onVerify) onVerify(token);
              injectTokenIntoForm(token);
            })
            .catch((err: any) => {
              console.error('reCAPTCHA execution failed:', err);
            });
        } catch (err) {
          console.error('Error executing reCAPTCHA:', err);
        }
      });
    }

    function injectTokenIntoForm(token: string) {
      // Insert token into hidden input
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = token;
      }
      
      // Also find the relevant form and inject the token if needed
      const targetForm = formId 
        ? document.getElementById(formId) as HTMLFormElement
        : document.querySelector('form');
        
      if (targetForm) {
        let input = targetForm.querySelector('input[name="g-recaptcha-response"]') as HTMLInputElement;
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'g-recaptcha-response';
          targetForm.appendChild(input);
        }
        input.value = token;
      }
    }

    return () => {
      // Clean up script when component unmounts
      window.onRecaptchaLoad = () => {}; // Clear callback
    };
  }, [enabled, siteKey, onVerify, formId]);

  // Detect if the site key appears to be v3
  const isV3 = siteKey?.startsWith('6L');

  return (
    <Fragment>
      {children}
      {enabled && siteKey && (
        <>
          {!isV3 && (
            <div className="mt-4">
              <div ref={recaptchaRef} />
            </div>
          )}
          <input 
            ref={hiddenInputRef} 
            type="hidden" 
            name="g-recaptcha-response" 
          />
        </>
      )}
    </Fragment>
  );
}
